import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'

const TIPOS = [
  { id: 'general', label: 'Novedad', icon: '📝', color: 'var(--gold)' },
  { id: 'recordatorio', label: 'Recordatorio', icon: '⏰', color: '#8b8fd4' },
  { id: 'llave', label: 'Llave/Objeto', icon: '🔑', color: '#6db8a0' },
  { id: 'pendiente', label: 'Pendiente', icon: '⚡', color: '#e8a04c' },
]

const ESTADOS = [
  { id: 'activa', label: 'Activa', color: 'var(--green)' },
  { id: 'resuelta', label: 'Resuelta', color: 'var(--text3)' },
  { id: 'archivada', label: 'Archivada', color: 'var(--text3)' },
]

function tipoInfo(id) { return TIPOS.find(t => t.id === id) || TIPOS[0] }
function estadoInfo(id) { return ESTADOS.find(e => e.id === id) || ESTADOS[0] }

function toISO(date) {
  return date.toISOString().split('T')[0]
}

function formatFecha(dateStr) {
  const d = new Date(dateStr)
  return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function formatFechaCorta(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  return date.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })
}

export default function Novedades({ usuario }) {
  const { usuario: authUsuario } = useAuth()
  const isAdmin = usuario?.rol === 'admin'

  const [novedades, setNovedades] = useState([])
  const [oficinas, setOficinas] = useState([])
  const [edificios, setEdificios] = useState([])
  const [edificioSeleccionado, setEdificioSeleccionado] = useState(null)
  const [loading, setLoading] = useState(true)

  const [filtros, setFiltros] = useState({ tipo: '', estado: 'activa' })

  const [modal, setModal] = useState(null) // 'nueva' | { detalle: nov }
  const [form, setForm] = useState({
    tipo: 'general',
    titulo: '',
    descripcion: '',
    oficina_id: '',
    llave_dejada_por: '',
    llave_retira: '',
    recordar_manana: false,
  })
  const [saving, setSaving] = useState(false)
  const [msgOk, setMsgOk] = useState('')

  // Determinar edificio activo
  const edificioActivoId = isAdmin
    ? edificioSeleccionado
    : (authUsuario?.edificios?.id || null)

  // Carga inicial de edificios (solo admin) y oficinas
  useEffect(() => {
    const init = async () => {
      if (isAdmin) {
        const { data: edifs } = await supabase.from('edificios').select('id, nombre').order('nombre')
        setEdificios(edifs || [])
        if (edifs && edifs.length && !edificioSeleccionado) {
          setEdificioSeleccionado(edifs[0].id)
        }
      }
    }
    init()
  }, [isAdmin, edificioSeleccionado])

  // Cargar oficinas del edificio activo
  useEffect(() => {
    if (!edificioActivoId) return
    supabase.from('oficinas')
      .select('id, numero, titular')
      .eq('activa', true)
      .eq('edificio_id', edificioActivoId)
      .order('numero')
      .then(({ data }) => setOficinas(data || []))
  }, [edificioActivoId])

  // Cargar novedades
  const cargar = useCallback(async () => {
    if (!edificioActivoId) return
    setLoading(true)
    let q = supabase.from('novedades')
      .select('*, oficina:oficinas(numero, titular), creador:usuarios!novedades_creado_por_fkey(nombre)')
      .eq('edificio_id', edificioActivoId)
      .order('created_at', { ascending: false })
      .limit(200)

    if (filtros.tipo) q = q.eq('tipo', filtros.tipo)
    if (filtros.estado) q = q.eq('estado', filtros.estado)

    const { data } = await q
    setNovedades(data || [])
    setLoading(false)
  }, [edificioActivoId, filtros])

  useEffect(() => { cargar() }, [cargar])

  // Recordatorios para hoy
  const hoy = toISO(new Date())
  const recordatoriosHoy = novedades.filter(n =>
    n.recordar_dia === hoy && n.estado === 'activa'
  )

  // Guardar novedad
  const guardar = async () => {
    if (!form.titulo.trim()) return
    if (!edificioActivoId) return
    setSaving(true)

    const recordarDia = form.recordar_manana
      ? toISO(new Date(Date.now() + 24 * 60 * 60 * 1000))
      : null

    const { error } = await supabase.from('novedades').insert({
      edificio_id: edificioActivoId,
      tipo: form.tipo,
      titulo: form.titulo.trim(),
      descripcion: form.descripcion.trim() || null,
      oficina_id: form.oficina_id || null,
      llave_dejada_por: form.tipo === 'llave' ? (form.llave_dejada_por.trim() || null) : null,
      llave_retira: form.tipo === 'llave' ? (form.llave_retira.trim() || null) : null,
      recordar_dia: recordarDia,
      creado_por: usuario.id,
    })

    if (!error) {
      setMsgOk('✅ Novedad registrada')
      setTimeout(() => setMsgOk(''), 3000)
      setModal(null)
      setForm({ tipo: 'general', titulo: '', descripcion: '', oficina_id: '', llave_dejada_por: '', llave_retira: '', recordar_manana: false })
      cargar()
    }
    setSaving(false)
  }

  // Marcar como resuelta
  const resolver = async (id) => {
    await supabase.from('novedades').update({
      estado: 'resuelta',
      resuelta_por: usuario.id,
      resuelta_en: new Date().toISOString(),
    }).eq('id', id)
    cargar()
  }

  // Archivar
  const archivar = async (id) => {
    if (!window.confirm('¿Archivar esta novedad?')) return
    await supabase.from('novedades').update({ estado: 'archivada' }).eq('id', id)
    cargar()
  }

  // Reactivar
  const reactivar = async (id) => {
    await supabase.from('novedades').update({
      estado: 'activa',
      resuelta_por: null,
      resuelta_en: null,
    }).eq('id', id)
    cargar()
  }

  return (
    <div style={{ padding: '24px 16px', maxWidth: 900, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 22, color: 'var(--gold)', letterSpacing: 1 }}>Novedades</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text3)' }}>Libro de turno · Recepción</p>
          </div>
          <button className="btn btn-gold btn-sm" onClick={() => setModal('nueva')}>+ Nueva novedad</button>
        </div>

        {/* Selector de edificio (solo admin) */}
        {isAdmin && edificios.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <select
              className="input input-sm"
              value={edificioSeleccionado || ''}
              onChange={e => setEdificioSeleccionado(e.target.value)}
              style={{ maxWidth: 240 }}
            >
              {edificios.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
            </select>
          </div>
        )}
      </div>

      {msgOk && (
        <div style={{ background: '#1a2e1a', border: '1px solid var(--green)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: 'var(--green)', fontSize: 14 }}>
          {msgOk}
        </div>
      )}

      {/* Banner de recordatorios para hoy */}
      {recordatoriosHoy.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #2a2540, #1a1a2e)',
          border: '1px solid #8b8fd4',
          borderRadius: 12,
          padding: '16px 20px',
          marginBottom: 20,
        }}>
          <div style={{ fontSize: 12, color: '#8b8fd4', fontWeight: 700, letterSpacing: 1, marginBottom: 8 }}>
            ⏰ RECORDATORIOS PARA HOY ({recordatoriosHoy.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {recordatoriosHoy.map(n => {
              const t = tipoInfo(n.tipo)
              return (
                <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                  <span style={{ fontSize: 14 }}>{t.icon}</span>
                  <span style={{ color: 'var(--text1)', fontWeight: 500 }}>{n.titulo}</span>
                  {n.oficina && <span style={{ color: 'var(--text3)', fontSize: 12 }}>· Of. {n.oficina.numero}</span>}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <select className="input input-sm" value={filtros.tipo} onChange={e => setFiltros(f => ({ ...f, tipo: e.target.value }))} style={{ minWidth: 160 }}>
          <option value="">Todos los tipos</option>
          {TIPOS.map(t => <option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
        </select>
        <select className="input input-sm" value={filtros.estado} onChange={e => setFiltros(f => ({ ...f, estado: e.target.value }))} style={{ minWidth: 140 }}>
          <option value="">Todos los estados</option>
          {ESTADOS.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
        </select>
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>Cargando...</div>
      ) : novedades.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>
          Sin novedades {filtros.estado && `${filtros.estado}s`}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {novedades.map(n => {
            const t = tipoInfo(n.tipo)
            const e = estadoInfo(n.estado)
            const esResuelta = n.estado === 'resuelta'
            const esArchivada = n.estado === 'archivada'

            return (
              <div key={n.id} style={{
                background: 'var(--card)',
                borderRadius: 10,
                padding: '14px 16px',
                border: `1px solid ${esArchivada ? '#2a2a2a' : t.color + '33'}`,
                opacity: esArchivada ? 0.55 : esResuelta ? 0.75 : 1,
                borderLeft: `3px solid ${t.color}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 16 }}>{t.icon}</span>
                      <span style={{ fontSize: 10, color: t.color, fontWeight: 700, letterSpacing: 1 }}>{t.label.toUpperCase()}</span>
                      {n.recordar_dia && (
                        <span style={{ fontSize: 10, color: '#8b8fd4', background: '#8b8fd422', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
                          ⏰ Recordar {n.recordar_dia === hoy ? 'hoy' : formatFechaCorta(n.recordar_dia)}
                        </span>
                      )}
                      {esResuelta && (
                        <span style={{ fontSize: 10, color: 'var(--green)', background: '#1a2e1a', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
                          ✓ Resuelta
                        </span>
                      )}
                      {esArchivada && (
                        <span style={{ fontSize: 10, color: 'var(--text3)', background: '#1a1a1a', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
                          Archivada
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 15, color: 'var(--text1)', fontWeight: 600, marginBottom: 4 }}>
                      {n.titulo}
                    </div>
                    {n.descripcion && (
                      <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 6, whiteSpace: 'pre-wrap' }}>
                        {n.descripcion}
                      </div>
                    )}

                    {/* Datos de llave */}
                    {n.tipo === 'llave' && (n.llave_dejada_por || n.llave_retira) && (
                      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--text3)', marginTop: 6, flexWrap: 'wrap' }}>
                        {n.llave_dejada_por && <span><b style={{ color: 'var(--text2)' }}>Dejado por:</b> {n.llave_dejada_por}</span>}
                        {n.llave_retira && <span><b style={{ color: 'var(--text2)' }}>Lo retira:</b> {n.llave_retira}</span>}
                      </div>
                    )}

                    {n.oficina && (
                      <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>
                        🏢 Of. {n.oficina.numero} — {n.oficina.titular}
                      </div>
                    )}

                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>
                      {formatFecha(n.created_at)} · {n.creador?.nombre || 'Recepción'}
                    </div>
                  </div>

                  {/* Acciones */}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {n.estado === 'activa' && (
                      <>
                        <button className="btn btn-ghost btn-sm" onClick={() => resolver(n.id)} style={{ fontSize: 11, color: 'var(--green)' }}>
                          ✓ Resolver
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => archivar(n.id)} style={{ fontSize: 11, color: 'var(--text3)' }}>
                          Archivar
                        </button>
                      </>
                    )}
                    {(n.estado === 'resuelta' || n.estado === 'archivada') && (
                      <button className="btn btn-ghost btn-sm" onClick={() => reactivar(n.id)} style={{ fontSize: 11 }}>
                        Reactivar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* ─── MODAL NUEVA NOVEDAD ─── */}
      {modal === 'nueva' && (
        <div style={{ position: 'fixed', inset: 0, background: '#000a', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setModal(null)}>
          <div style={{ background: 'var(--bg2)', borderRadius: 14, padding: 24, width: '100%', maxWidth: 480, border: '1px solid var(--border)', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 20px', color: 'var(--gold)', fontSize: 16 }}>Nueva novedad</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Tipo */}
              <div>
                <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 8 }}>Tipo *</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                  {TIPOS.map(t => (
                    <button key={t.id}
                      onClick={() => setForm(f => ({ ...f, tipo: t.id }))}
                      style={{
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: form.tipo === t.id ? `1.5px solid ${t.color}` : '1px solid var(--border)',
                        background: form.tipo === t.id ? `${t.color}22` : 'transparent',
                        color: form.tipo === t.id ? t.color : 'var(--text2)',
                        cursor: 'pointer',
                        fontSize: 13,
                        fontWeight: form.tipo === t.id ? 600 : 400,
                        display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-start',
                      }}>
                      <span style={{ fontSize: 16 }}>{t.icon}</span>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Título */}
              <div>
                <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 6 }}>Título *</label>
                <input className="input" placeholder="Breve descripción" value={form.titulo}
                  onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))} />
              </div>

              {/* Descripción */}
              <div>
                <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 6 }}>Descripción</label>
                <textarea className="input" placeholder="Detalle (opcional)" rows={3} value={form.descripcion}
                  onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
                  style={{ resize: 'vertical', fontFamily: 'inherit' }} />
              </div>

              {/* Campos llave */}
              {form.tipo === 'llave' && (
                <>
                  <div>
                    <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 6 }}>Dejado por</label>
                    <input className="input" placeholder="Nombre de quien lo dejó" value={form.llave_dejada_por}
                      onChange={e => setForm(f => ({ ...f, llave_dejada_por: e.target.value }))} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 6 }}>Lo retira</label>
                    <input className="input" placeholder="Nombre de quien debe retirarlo" value={form.llave_retira}
                      onChange={e => setForm(f => ({ ...f, llave_retira: e.target.value }))} />
                  </div>
                </>
              )}

              {/* Oficina (opcional) */}
              <div>
                <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 6 }}>Oficina relacionada (opcional)</label>
                <select className="input" value={form.oficina_id} onChange={e => setForm(f => ({ ...f, oficina_id: e.target.value }))}>
                  <option value="">— Sin oficina —</option>
                  {oficinas.map(o => <option key={o.id} value={o.id}>{o.numero} — {o.titular}</option>)}
                </select>
              </div>

              {/* Recordar mañana */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '10px 12px', background: form.recordar_manana ? '#8b8fd422' : 'var(--bg3)', borderRadius: 8, border: `1px solid ${form.recordar_manana ? '#8b8fd4' : 'var(--border)'}` }}>
                <input
                  type="checkbox"
                  checked={form.recordar_manana}
                  onChange={e => setForm(f => ({ ...f, recordar_manana: e.target.checked }))}
                  style={{ width: 16, height: 16, accentColor: '#8b8fd4', cursor: 'pointer' }}
                />
                <span style={{ fontSize: 13, color: form.recordar_manana ? '#8b8fd4' : 'var(--text2)', fontWeight: form.recordar_manana ? 600 : 400 }}>
                  ⏰ Recordar al día siguiente
                </span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancelar</button>
              <button className="btn btn-gold" onClick={guardar} disabled={saving || !form.titulo.trim()}>
                {saving ? 'Guardando...' : '✓ Registrar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
